import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import type { ApiRequestLogDtoT, WebhookDeliveryDtoT } from '@wave/shared';
import { useApiKeys, useApiRequests, useRetryDelivery, useWebhookDeliveries } from '../../api/integration';
import {
  btnPrimarySm,
  filterActive,
  filterIdle,
  sectionTitle,
  selectCls,
  tableCell,
  tableCellNum,
  tableCls,
  tableHead,
  tableHeadCell,
  tableHeadCellRight,
  tableRow,
  tableWrapper,
} from '../../lib/styles';
import { WEBHOOK_EVENT_LABELS } from './labels';

type DeliveryStatusFilter = 'all' | WebhookDeliveryDtoT['status'];

const DELIVERY_STATUS_OPTIONS: { value: DeliveryStatusFilter; label: string }[] = [
  { value: 'all', label: 'Все' },
  { value: 'pending', label: 'Pending' },
  { value: 'success', label: 'Success' },
  { value: 'failed', label: 'Failed' },
];

/**
 * Вкладка «Журнал» страницы /admin/api (задача 10) — две секции: журнал запросов
 * к публичному API (в т.ч. неудачные попытки аутентификации) и статус доставки
 * вебхуков с ручным ретраем для failed-записей.
 */
export function ApiLogTab() {
  return (
    <div className="space-y-8">
      <RequestsSection />
      <DeliveriesSection />
    </div>
  );
}

function RequestsSection() {
  const { data: keys } = useApiKeys();
  const [keyId, setKeyId] = useState('all');
  const { data: requests, isLoading } = useApiRequests({ keyId: keyId === 'all' ? undefined : keyId });

  return (
    <section className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className={sectionTitle}>Запросы</h2>
        <select value={keyId} onChange={(e) => setKeyId(e.target.value)} className={`${selectCls} w-56`}>
          <option value="all">Все ключи</option>
          {(keys ?? []).map((k) => (
            <option key={k.id} value={k.id}>
              {k.name}
            </option>
          ))}
        </select>
      </div>

      {isLoading ? (
        <div className="text-sm text-text2">Загрузка…</div>
      ) : (
        <div className={tableWrapper}>
          <table className={tableCls}>
            <thead>
              <tr className={tableHead}>
                <th className={tableHeadCell}>Время</th>
                <th className={tableHeadCell}>Ключ</th>
                <th className={tableHeadCell}>Метод и путь</th>
                <th className={tableHeadCell}>Статус</th>
                <th className={tableHeadCell}>Заявка</th>
                <th className={tableHeadCell}>Ошибка</th>
                <th className={tableHeadCellRight}>мс</th>
              </tr>
            </thead>
            <tbody>
              {(requests ?? []).length === 0 && (
                <tr>
                  <td colSpan={7} className="px-3 py-12 text-center text-sm text-text2">
                    Запросов пока нет
                  </td>
                </tr>
              )}
              {(requests ?? []).map((r) => (
                <RequestRow key={r.id} req={r} />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

function RequestRow({ req }: { req: ApiRequestLogDtoT }) {
  const navigate = useNavigate();
  const location = useLocation();
  const isOk = req.statusCode >= 200 && req.statusCode < 300;

  return (
    <tr className={tableRow}>
      <td className={`${tableCell} font-mono text-xs text-text2`}>{formatDateTime(req.createdAt)}</td>
      <td className={tableCell}>
        {req.keyName ? (
          <span className="text-text">{req.keyName}</span>
        ) : (
          <span className="text-text-subtle">{req.keyPrefixAttempted ?? '—'}</span>
        )}
      </td>
      <td className={`${tableCell} font-mono text-xs`}>
        <span className="text-text2">{req.method}</span> <span className="text-text">{req.path}</span>
      </td>
      <td className={tableCell}>
        <span className={`font-mono text-sm font-semibold ${isOk ? 'text-success' : 'text-danger'}`}>
          {req.statusCode}
        </span>
      </td>
      <td className={tableCell}>
        {req.orderId ? (
          <button
            className="font-mono text-sm font-semibold text-accent transition-colors duration-150 hover:text-accent2 hover:underline underline-offset-4"
            onClick={() => navigate(`/orders/${req.orderId}`, { state: { backgroundLocation: location } })}
          >
            {req.orderId}
          </button>
        ) : (
          <span className="text-text-subtle">—</span>
        )}
      </td>
      <td className={`${tableCell} text-xs`}>
        {req.errorCode ? <span className="text-danger">{req.errorCode}</span> : <span className="text-text-subtle">—</span>}
      </td>
      <td className={`${tableCellNum} text-text2`}>{req.durationMs ?? '—'}</td>
    </tr>
  );
}

function DeliveriesSection() {
  const [status, setStatus] = useState<DeliveryStatusFilter>('all');
  const { data: deliveries, isLoading } = useWebhookDeliveries({ status: status === 'all' ? undefined : status });

  return (
    <section className="space-y-3">
      <h2 className={sectionTitle}>Webhook-доставки</h2>

      <div className="flex gap-1">
        {DELIVERY_STATUS_OPTIONS.map((opt) => (
          <button
            key={opt.value}
            onClick={() => setStatus(opt.value)}
            className={status === opt.value ? filterActive : filterIdle}
          >
            {opt.label}
          </button>
        ))}
      </div>

      {isLoading ? (
        <div className="text-sm text-text2">Загрузка…</div>
      ) : (
        <div className={tableWrapper}>
          <table className={tableCls}>
            <thead>
              <tr className={tableHead}>
                <th className={tableHeadCell}>Время</th>
                <th className={tableHeadCell}>Ключ</th>
                <th className={tableHeadCell}>Событие</th>
                <th className={tableHeadCell}>Заявка</th>
                <th className={tableHeadCell}>Статус</th>
                <th className={tableHeadCellRight}>Попытки</th>
                <th className={tableHeadCell}>Ретрай / доставка</th>
                <th className={tableHeadCell}>Ошибка</th>
                <th className={`${tableHeadCell} text-right`}>Действия</th>
              </tr>
            </thead>
            <tbody>
              {(deliveries ?? []).length === 0 && (
                <tr>
                  <td colSpan={9} className="px-3 py-12 text-center text-sm text-text2">
                    Доставок пока нет
                  </td>
                </tr>
              )}
              {(deliveries ?? []).map((d) => (
                <DeliveryRow key={d.id} delivery={d} />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

function DeliveryRow({ delivery: d }: { delivery: WebhookDeliveryDtoT }) {
  const navigate = useNavigate();
  const location = useLocation();
  const retry = useRetryDelivery();
  const [error, setError] = useState<string | null>(null);

  async function doRetry() {
    setError(null);
    try {
      await retry.mutateAsync(d.id);
    } catch (err) {
      setError((err as Error).message);
    }
  }

  return (
    <>
      <tr className={tableRow}>
        <td className={`${tableCell} font-mono text-xs text-text2`}>{formatDateTime(d.createdAt)}</td>
        <td className={tableCell}>
          {d.keyName ? <span className="text-text">{d.keyName}</span> : <span className="text-text-subtle">—</span>}
        </td>
        <td className={`${tableCell} text-xs text-text`}>{WEBHOOK_EVENT_LABELS[d.eventType]}</td>
        <td className={tableCell}>
          <button
            className="font-mono text-sm font-semibold text-accent transition-colors duration-150 hover:text-accent2 hover:underline underline-offset-4"
            onClick={() => navigate(`/orders/${d.orderId}`, { state: { backgroundLocation: location } })}
          >
            {d.orderId}
          </button>
        </td>
        <td className={tableCell}>
          <DeliveryStatusBadge status={d.status} />
        </td>
        <td className={`${tableCellNum} text-text2`}>{d.attempts}</td>
        <td className={`${tableCell} text-xs`}>
          {d.status === 'pending' && d.nextRetryAt ? (
            <span className="text-text2">→ {formatDateTime(d.nextRetryAt)}</span>
          ) : d.deliveredAt ? (
            <span className="text-success">✓ {formatDateTime(d.deliveredAt)}</span>
          ) : (
            <span className="text-text-subtle">—</span>
          )}
        </td>
        <td className={`${tableCell} max-w-xs truncate text-xs text-danger`} title={d.lastError ?? undefined}>
          {d.lastError ?? <span className="text-text-subtle">—</span>}
        </td>
        <td className={`${tableCell} text-right`}>
          {d.status === 'failed' && (
            <button onClick={() => void doRetry()} className={btnPrimarySm} disabled={retry.isPending}>
              ⟳ Повторить
            </button>
          )}
        </td>
      </tr>
      {error && (
        <tr>
          <td colSpan={9} className="border-b border-border-subtle bg-bg/50 px-3 py-2">
            <div className="rounded border border-[var(--red-border)] bg-[var(--red-bg)] px-2 py-1 text-xs text-danger">
              {error}
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

function DeliveryStatusBadge({ status }: { status: WebhookDeliveryDtoT['status'] }) {
  const map: Record<WebhookDeliveryDtoT['status'], string> = {
    pending: 'border-border bg-bg3 text-text2',
    success: 'border-[var(--green-border)] bg-[var(--green-bg)] text-success',
    failed: 'border-[var(--red-border)] bg-[var(--red-bg)] text-danger',
  };
  return (
    <span className={`inline-flex items-center rounded-md border px-2 py-0.5 text-[11px] font-medium ${map[status]}`}>
      {status}
    </span>
  );
}

function formatDateTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString('ru-RU', { hour12: false });
}
